import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Avatar,
  Divider,
  Paper, // Used for the card-like sections
  Stack,
  IconButton,
  Grid,
  createTheme,
  ThemeProvider,
  CssBaseline,
  Container,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import MedicalInformationOutlinedIcon from '@mui/icons-material/MedicalInformationOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import { useDrawer } from '../../contexts/DrawerContext'
import { get } from '../../utility/fetch';

// --- Custom MUI Theme (similar to previous, adjusted for this page's specifics) ---
const theme = createTheme({
  palette: {
    primary: {
      main: '#28a745', // Green for accents and buttons
    },
    secondary: {
      main: '#f8f9fa', // Light background for the page
    },
    background: {
      default: '#f0f2f5', // Very light grey background
      paper: '#fff', // White background for cards and panels
    },
    text: {
      primary: '#333',
      secondary: '#6c757d',
    },
  },
  typography: {
    fontFamily: 'Roboto, sans-serif',
    h5: {
      fontWeight: 600,
      fontSize: '1.5rem',
    },
    h6: {
      fontWeight: 600,
      fontSize: '1.1rem',
    },
    body1: {
      fontSize: '0.95rem',
      lineHeight: 1.6,
    },
    body2: {
      fontSize: '0.85rem',
      color: '#6c757d',
    },
    caption: {
      fontSize: '0.75rem',
      color: '#6c757d',
    },
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: '16px', // Rounded corners for sections
          boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.05)', // Subtle shadow
          p: 3, // Default padding for Paper components
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: '', // More rectangular rounded buttons
          padding: '8px 12px', // Generous padding
          fontWeight: 600,
        },
        containedPrimary: {
          backgroundColor: '#28a745',
          color: '#fff',
          '&:hover': {
            backgroundColor: '#218838',
          },
          boxShadow: 'none', // No default shadow
        },
        outlinedPrimary: {
          borderColor: '#28a745',
          color: '#28a745',
          '&:hover': {
            borderColor: '#218838',
            color: '#218838',
            backgroundColor: 'rgba(40, 167, 69, 0.04)',
          }
        }
      },
    },
  },
});

function ConsultationSummary() {
  const meetingSummary = `It is a long established fact that a reader will be distracted by the readable content of a page when looking at its layout. The point of using Lorem Ipsum is that it has a more-or-less normal distribution of letters, as opposed to using 'Content here, content here', making it look like readable English.`;

  const diagnosis = `Many desktop publishing packages and web page editors now use Lorem Ipsum as their default model text, and a search for 'lorem ipsum' will uncover many web sites still in their infancy. Various versions have evolved over the years, sometimes by accident, sometimes on purpose (injected humour and the like). There are many variations of passages of Lorem Ipsum available, but the majority have suffered alteration in some form, by injected humour, or randomised words which don't look even slightly believable. If you are going to use a passage of Lorem Ipsum, you need to be sure there isn't anything embarrassing hidden in the middle of text. All the Lorem Ipsum generators on the Internet tend to repeat predefined chunks as necessary, making this the first true generator on the Internet. It uses a dictionary of over 200 Latin words, combined with a handful of model sentence structures, to generate Lorem Ipsum which looks reasonable. The generated Lorem Ipsum is therefore always free from repetition, injected humour, or non-characteristic words etc.`;

  const nextSteps = [
    'Immediate admission to the clinic',
    'Run some lab work',
    'Prescriptions',
  ];

  const [prescriptionData, setprescriptionData] = useState([]);
  const [labWorkData, setLabWorkData] = useState([]);
  const [followUpData, setFollowUpData] = useState();
  const [admissionData, setAdmissionData] = useState([])
  const [appointmentDetails, setAppointmentDetails] = useState([]);


  const query = new URLSearchParams(window.location.search)


  const getPharmResults = async () => {
    let res = await get(`/treatment/appointment/${query?.get("kkd")}`)
    console.log(res);
  }

  const getLabResults = async () => {
    let res = await get(`/lab/list/${query?.get("kkd")}/appointment`)
    console.log(res);
  }


  const getAppointment = async () => {
    let res = await get(`/appointment/${query?.get("kkd")}`);
    console.log(res)
    setAppointmentDetails(res?.data)

  }

  useEffect(() => {
    getPharmResults();
    getLabResults();
    getAppointment()
  }, [])

  const { toggleAdmissionDetailsDrawer, } = useDrawer();

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline /> {/* Resets CSS for consistent styling */}
      <Box sx={{ backgroundColor: theme.palette.background.default, minHeight: '100vh', pb: 4 }}>
        {/* Header */}
        <Box sx={{ p: 3, display: 'flex', alignItems: 'center', backgroundColor: theme.palette.background.default }}>
          <IconButton sx={{ mr: 2 }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Consultation Summary
          </Typography>
        </Box>

        <Container maxWidth="lg" sx={{ mt: 2 }}>
          <Grid wrap='nowrap' container spacing={2}>
            {/* Left Column: Meeting Summary, Diagnosis, Next steps */}
            <Grid item xs={12} md={8}>
              <Stack spacing={3}>
                {/* Meeting Summary */}
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Meeting Summary:
                  </Typography>
                  <Typography variant="body1">
                    {meetingSummary}
                  </Typography>
                </Box>

                {/* Diagnosis */}
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Diagnosis
                  </Typography>
                  <Typography variant="body1">
                    {diagnosis}
                  </Typography>
                </Box>

                {/* Next steps */}
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Next steps:
                  </Typography>
                  <Box component="ul" sx={{ pl: 2, mt: 1, listStyle: 'none' }}> {/* Remove default list styling */}
                    {nextSteps.map((step, index) => (
                      <Typography key={index} component="li" variant="body1" sx={{ mb: 1 }}>
                        - {step}
                      </Typography>
                    ))}
                  </Box>
                </Box>
              </Stack>
            </Grid>

            {/* Right Column: Doctor Info and Action Buttons */}
            <Grid item xs={12} md={4}>
              <Box sx={{ p: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                {/* <Typography variant="body2" color="text.secondary">
                    Meeting Summary
                </Typography> */}
                <Avatar
                  alt="Dr. Ember Wynn"
                  src="https://i.imgur.com/EmberWynn.jpg" // Placeholder for Dr. Ember Wynn's avatar
                  sx={{ width: 100, height: 100, mb: 1 }}
                />
                <Typography variant="h6" gutterBottom>
                  {appointmentDetails?.doctor}
                </Typography>
                <Box sx={{
                  backgroundColor: theme.palette.primary.main,
                  color: 'white',
                  px: 2,
                  py: 0.5,
                  borderRadius: '8px',
                  mb: 2,
                  display: 'inline-block',
                  fontSize: '0.8rem',
                  fontWeight: 500,
                }}>
                  {new Date(appointmentDetails?.appointDateTime).toLocaleDateString()} |  {new Date(appointmentDetails?.appointDateTime).toLocaleTimeString()}
                </Box>

                <Stack spacing={2} sx={{ width: '100%', mt: 2, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <Button onClick={toggleAdmissionDetailsDrawer(true, appointmentDetails?.doctorId, "chat")} sx={{ width: "50%" }} variant="outlined" startIcon={<ChatBubbleOutlineIcon />} >
                    View Chats
                  </Button>
                  <Button onClick={toggleAdmissionDetailsDrawer(true, 4, "admission", admissionData)} sx={{ width: "50%" }} variant="contained" fullWidth>
                    View Admission Details
                  </Button>
                  <Button onClick={toggleAdmissionDetailsDrawer(true, 4, "pharmacy", prescriptionData)} sx={{ width: "50%" }} variant="outlined" startIcon={<ReceiptLongOutlinedIcon />} fullWidth>
                    View Prescriptions
                  </Button>
                  <Button onClick={toggleAdmissionDetailsDrawer(true, 4, "labWork", labWorkData)} sx={{ width: "50%" }} variant="outlined" startIcon={<ScienceOutlinedIcon />} fullWidth>
                    View Lab Work Request
                  </Button>
                  <Button onClick={toggleAdmissionDetailsDrawer(true, 4, "followup", followUpData)} sx={{ width: "50%" }} variant="outlined" startIcon={<CalendarMonthOutlinedIcon />} fullWidth>
                    View Follow-Up Appointment
                  </Button>
                </Stack>

                <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
                  Rate your experience with {appointmentDetails?.doctor}
                </Typography>
                {/* Star rating component can be added here (e.g., using @mui/material/Rating) */}
                {/* For example: <Rating name="doctor-rating" value={null} precision={0.5} /> */}
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </ThemeProvider>
  );
}

export default ConsultationSummary;